package com.havn.app.features.tasks.domain

data class HavnTask(
    val id: String,
    val title: String,
    val description: String? = null,
    val type: HavnTaskType,
    val status: HavnTaskStatus,
    val priority: HavnTaskPriority,
    val dueAt: String? = null,
    val completedAt: String? = null,
    val assignedToUserId: String? = null,
    val createdByUserId: String,
    val agencyId: String? = null,
    val propertyId: String? = null,
    val contactId: String? = null,
    val openHouseId: String? = null,
    val leadId: String? = null,
    val followUpId: String? = null,
    val aiGenerated: Boolean = false,
    val notes: String? = null,
    val createdAt: String? = null,
    val updatedAt: String? = null
)

data class HavnTaskPage(
    val tasks: List<HavnTask>,
    val totalCount: Int
)

data class HavnTaskDraft(
    val title: String,
    val description: String? = null,
    val type: HavnTaskType,
    val status: HavnTaskStatus = HavnTaskStatus.Open,
    val priority: HavnTaskPriority = HavnTaskPriority.Medium,
    val dueAt: String? = null,
    val assignedToUserId: String? = null,
    val agencyId: String? = null,
    val propertyId: String? = null,
    val contactId: String? = null,
    val openHouseId: String? = null,
    val leadId: String? = null,
    val followUpId: String? = null,
    val aiGenerated: Boolean = false,
    val notes: String? = null
)

data class AiSuggestedTask(
    val id: String,
    val draft: HavnTaskDraft,
    val reason: String
)

enum class HavnTaskType(val label: String, val databaseValue: String) {
    FollowUpLead("Follow up lead", "follow_up_lead"),
    CallBuyer("Call buyer", "call_buyer"),
    SendBrochure("Send brochure", "send_brochure"),
    SchedulePrivateTour("Schedule private tour", "schedule_private_tour"),
    PrepareSellerReport("Prepare seller report", "prepare_seller_report"),
    ReviewAiFollowUp("Review AI follow-up", "review_ai_follow_up"),
    UpdatePropertyDetails("Update property details", "update_property_details"),
    SetupKioskMode("Setup kiosk mode", "setup_kiosk_mode"),
    InviteAgent("Invite agent", "invite_agent"),
    CollectSellerFeedback("Collect seller feedback", "collect_seller_feedback"),
    Custom("Custom", "custom");

    companion object {
        fun fromDatabase(value: String?): HavnTaskType = entries.firstOrNull { it.databaseValue == value } ?: Custom
    }
}

enum class HavnTaskStatus(val label: String, val databaseValue: String) {
    Open("Open", "open"),
    InProgress("In progress", "in_progress"),
    Done("Done", "done"),
    Overdue("Overdue", "overdue"),
    Cancelled("Cancelled", "cancelled");

    companion object {
        fun fromDatabase(value: String?): HavnTaskStatus = entries.firstOrNull { it.databaseValue == value } ?: Open
    }
}

enum class HavnTaskPriority(val label: String, val databaseValue: String) {
    Low("Low", "low"),
    Medium("Medium", "medium"),
    High("High", "high"),
    Urgent("Urgent", "urgent");

    companion object {
        fun fromDatabase(value: String?): HavnTaskPriority = entries.firstOrNull { it.databaseValue == value } ?: Medium
    }
}

interface HavnTaskRepository {
    suspend fun taskPage(
        agentId: String,
        agencyId: String? = null,
        statuses: List<HavnTaskStatus> = emptyList(),
        offset: Int = 0,
        limit: Int = 20,
        personalScope: Boolean = false
    ): Result<HavnTaskPage>
    suspend fun createTask(agentId: String, draft: HavnTaskDraft): Result<HavnTask>
    suspend fun updateTask(agentId: String, taskId: String, draft: HavnTaskDraft): Result<HavnTask>
    suspend fun updateStatus(agentId: String, taskId: String, status: HavnTaskStatus, completedAt: String? = null): Result<HavnTask>
}
